import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/hooks/useFavorites";
import { useCompare } from "@/hooks/useCompare";
import { motion, AnimatePresence } from "framer-motion";
import { LogIn, LayoutDashboard, ShieldCheck, LogOut, UserPlus, Heart, GitCompareArrows, Zap, Menu, X, UserCog } from "lucide-react";
import Logo from "./Logo";

export default function Header() {
  const { user, logout } = useAuth();
  const { count: favCount } = useFavorites();
  const { count: cmpCount } = useCompare();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  // Lock body scroll while menu open
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const onLogout = () => { logout(); nav("/"); };

  return (
    <header className="bg-[var(--card)] border-b border-[var(--hairline)]">
      <div className="page-wrap flex items-center justify-between h-[64px] md:h-[72px]">
        <Link to="/" data-testid="logo-link" className="block flex-shrink-0">
          <Logo size="md" className="h-7 md:h-8" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
          <NavLink to="/fahrzeuge" data-testid="nav-marketplace" className={({isActive}) => `font-display font-semibold text-[14px] px-3 py-2 transition-colors ${isActive ? "text-[var(--green)] bg-[var(--green-subtle)]" : "text-[var(--fg2)] hover:text-[var(--fg1)] hover:bg-[var(--muted-fill)]"}`}>
            Fahrzeuge
          </NavLink>
          <DesktopBadgeLink to="/vergleich" testid="nav-compare" icon={GitCompareArrows} label="Vergleich" count={cmpCount} badgeTestid="compare-count" />
          <DesktopBadgeLink to="/favoriten" testid="nav-favorites" icon={Heart} label="Merkliste" count={favCount} filled badgeTestid="favorites-count" />

          {!user && (
            <>
              <Link to="/login" data-testid="nav-login" className="inline-flex items-center gap-2 font-display font-semibold text-[14px] text-[var(--fg2)] hover:text-[var(--fg1)] px-3 py-2 hover:bg-[var(--muted-fill)] transition-colors">
                <LogIn className="w-4 h-4" /> Login
              </Link>
              <Link to="/register" data-testid="nav-register" className="btn btn-primary btn-sm ml-1">
                <Zap className="w-3.5 h-3.5" /> Inserat aufgeben
              </Link>
            </>
          )}
          {user && ["dealer", "private"].includes(user.role) && (
            <Link to="/dashboard" data-testid="nav-dashboard" className="inline-flex items-center gap-2 font-display font-semibold text-[14px] text-[var(--fg2)] hover:text-[var(--fg1)] px-3 py-2 hover:bg-[var(--muted-fill)] transition-colors">
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </Link>
          )}
          {user && user.role !== "admin" && (
            <Link to="/profil" data-testid="nav-profile" className="inline-flex items-center gap-2 font-display font-semibold text-[14px] text-[var(--fg2)] hover:text-[var(--fg1)] px-3 py-2 hover:bg-[var(--muted-fill)] transition-colors">
              <UserCog className="w-4 h-4" /> Profil
            </Link>
          )}
          {user && user.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className="inline-flex items-center gap-2 font-display font-semibold text-[14px] text-[var(--fg2)] hover:text-[var(--fg1)] px-3 py-2 hover:bg-[var(--muted-fill)] transition-colors">
              <ShieldCheck className="w-4 h-4" /> Admin
            </Link>
          )}
          {user && (
            <button data-testid="logout-btn" onClick={onLogout} className="inline-flex items-center gap-2 font-display font-semibold text-[14px] text-[var(--fg2)] hover:text-[var(--green)] px-3 py-2 hover:bg-[var(--muted-fill)] transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          )}
        </nav>

        {/* Mobile/Tablet: compact icons + hamburger */}
        <div className="flex lg:hidden items-center gap-1">
          <Link to="/favoriten" data-testid="m-nav-favorites" aria-label="Merkliste" className="relative w-10 h-10 inline-flex items-center justify-center text-[var(--fg2)] hover:text-[var(--green)]">
            <Heart className="w-5 h-5" fill={favCount > 0 ? "currentColor" : "none"} />
            {favCount > 0 && <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 text-[9px] font-bold bg-[var(--green)] text-white inline-flex items-center justify-center font-body">{favCount}</span>}
          </Link>
          {!user && (
            <Link to="/register" data-testid="m-nav-register" className="btn btn-primary btn-sm !px-3 !py-2 hidden sm:inline-flex">
              <Zap className="w-3.5 h-3.5" /> Inserat
            </Link>
          )}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Menü schließen" : "Menü öffnen"}
            className="w-10 h-10 inline-flex items-center justify-center text-[var(--fg1)] hover:bg-[var(--muted-fill)]"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 top-[64px] bg-black/30 z-40"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden absolute left-0 right-0 top-full bg-[var(--card)] border-b border-[var(--hairline)] shadow-lg z-50"
              data-testid="mobile-menu"
            >
              <div className="page-wrap py-3 flex flex-col">
                <MobileLink to="/fahrzeuge" icon={null} label="Fahrzeuge" testid="m-nav-marketplace" />
                <MobileLink to="/vergleich" icon={GitCompareArrows} label="Vergleich" count={cmpCount} testid="m-nav-compare" />
                <MobileLink to="/favoriten" icon={Heart} label="Merkliste" count={favCount} testid="m-nav-favorites-list" />
                {!user && <>
                  <div className="h-px bg-[var(--hairline)] my-2" />
                  <MobileLink to="/login" icon={LogIn} label="Login" testid="m-nav-login" />
                  <MobileLink to="/register" icon={UserPlus} label="Inserat aufgeben" testid="m-nav-register-link" highlight />
                </>}
                {user && ["dealer", "private"].includes(user.role) && (
                  <>
                    <div className="h-px bg-[var(--hairline)] my-2" />
                    <MobileLink to="/dashboard" icon={LayoutDashboard} label="Dashboard" testid="m-nav-dashboard" />
                  </>
                )}
                {user && user.role !== "admin" && (
                  <>
                    <MobileLink to="/profil" icon={UserCog} label="Profil bearbeiten" testid="m-nav-profile" />
                  </>
                )}
                {user && user.role === "admin" && (
                  <>
                    <div className="h-px bg-[var(--hairline)] my-2" />
                    <MobileLink to="/admin" icon={ShieldCheck} label="Admin" testid="m-nav-admin" />
                  </>
                )}
                {user && (
                  <button data-testid="m-logout-btn" onClick={onLogout} className="flex items-center gap-3 py-3 px-2 font-display font-semibold text-[15px] text-[var(--fg2)] hover:text-[var(--green)] text-left">
                    <LogOut className="w-5 h-5" /> Logout
                  </button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}

function DesktopBadgeLink({ to, testid, icon: Icon, label, count, filled = false, badgeTestid }) {
  return (
    <NavLink to={to} data-testid={testid} className={({isActive}) => `relative inline-flex items-center gap-1.5 px-3 py-2 font-display font-semibold text-[14px] transition-colors ${isActive ? "text-[var(--green)] bg-[var(--green-subtle)]" : "text-[var(--fg2)] hover:text-[var(--fg1)] hover:bg-[var(--muted-fill)]"}`} title={label}>
      <Icon className="w-4 h-4" fill={filled && count > 0 ? "currentColor" : "none"} />
      <span className="hidden xl:inline">{label}</span>
      {count > 0 && (
        <span data-testid={badgeTestid} className="ml-0.5 min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-[var(--green)] text-white inline-flex items-center justify-center font-body">{count}</span>
      )}
    </NavLink>
  );
}

function MobileLink({ to, icon: Icon, label, count, testid, highlight }) {
  return (
    <NavLink
      to={to} data-testid={testid}
      className={({isActive}) => `flex items-center gap-3 py-3 px-2 font-display font-semibold text-[15px] transition-colors ${isActive ? "text-[var(--green)]" : highlight ? "text-[var(--green)]" : "text-[var(--fg1)] hover:text-[var(--green)]"}`}
    >
      {Icon && <Icon className="w-5 h-5" />}
      <span className="flex-1">{label}</span>
      {count > 0 && <span className="min-w-[20px] h-5 px-1.5 text-[10px] font-bold bg-[var(--green)] text-white inline-flex items-center justify-center font-body">{count}</span>}
    </NavLink>
  );
}
